// Draft preview channel. The appearance editor keeps an unsaved draft in
// `localStorage`; the public site, when opened with `?preview=1`, renders that
// draft instead of the published one. Because the preview iframe is same-origin
// but a different document, the `storage` event delivers every update live —
// no reload, no backend round-trip.

import { parseStorefront, type StorefrontDocument } from '@/lib/storefront/document';

const STORAGE_KEY = 'fleetava:storefront-preview';

/** True when the current document was opened as an editor preview. */
export function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  const flag = new URLSearchParams(window.location.search).get('preview');
  return flag === '1' || flag === 'true';
}

export function writePreviewDocument(document: StorefrontDocument): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(document));
  } catch {
    // Private mode or a full quota: the preview keeps the published values.
  }
}

export function readPreviewDocument(): StorefrontDocument | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? parseStorefront(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function clearPreviewDocument(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up.
  }
}

/**
 * Calls `onChange` whenever the editor writes a new draft. The `storage` event
 * fires in every same-origin document except the one that wrote the value,
 * which is exactly the preview iframe.
 */
export function subscribePreviewDocument(
  onChange: (document: StorefrontDocument) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (event: StorageEvent) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    const document = readPreviewDocument();
    if (document) onChange(document);
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
